import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Icon } from "../../lib/icons";
import { ChevronLeft, ChevronRight, Store } from "lucide-react";

export default function ShopCategoriesSection() {
  const { settings: s } = useSettings();
  const c = s?.home_sections_content?.shop_categories || {};
  const [cats, setCats] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    api.get("/product-categories").then(r => setCats(r.data || [])).catch(() => setCats([]));
  }, []);

  if (!cats.length) return null;

  const scroll = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="shop-categories-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-semibold mb-2">
              {c.kicker || "Explorez"}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--brand-navy)]">
              {c.title || "Nos catégories"}
            </h2>
            {c.subtitle && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl">{c.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => scroll(-1)} className="w-9 h-9 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] flex items-center justify-center" aria-label="Précédent" data-testid="shop-categories-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => scroll(1)} className="w-9 h-9 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] flex items-center justify-center" aria-label="Suivant" data-testid="shop-categories-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={scroller} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar">
          {cats.map((cat) => (
            <Link
              key={cat.id}
              to={`/shop?category=${cat.slug}`}
              className="group relative flex-shrink-0 w-[220px] md:w-[260px] snap-start rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-white hover:shadow-xl transition-all"
              data-testid={`shop-category-${cat.slug}`}
            >
              <div className="aspect-[4/5] relative bg-gradient-to-br from-[color:var(--brand-navy)]/5 to-[color:var(--brand-sky)]/10">
                {cat.image_url ? (
                  <img src={cat.image_url} alt={cat.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name={cat.icon || "Store"} className="w-16 h-16 text-[color:var(--brand-navy)]/25" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <div className="font-bold text-lg">{cat.name}</div>
                {cat.description && <div className="text-[11px] opacity-90 line-clamp-2 mt-0.5">{cat.description}</div>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
